import React, { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Reusable Info Banner Component
 * - Dismissible with X button
 * - Stores dismissed state in sessionStorage (resets on new login/session)
 * - Consistent styling across all pages
 */
export const InfoBanner = ({ 
  id, // Unique identifier for sessionStorage
  children,
  className,
  variant = 'info' // info, warning, success
}) => {
  const storageKey = `info_banner_dismissed_${id}`;
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(storageKey);
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, [storageKey]);

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem(storageKey, 'true');
  };

  if (isDismissed) {
    return null;
  }

  const variantStyles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800'
  };

  const iconStyles = {
    info: 'text-blue-500',
    warning: 'text-amber-500',
    success: 'text-green-500'
  };

  return (
    <div 
      className={cn(
        "relative flex items-start gap-3 p-4 rounded-lg border mb-4",
        variantStyles[variant],
        className
      )}
      data-testid={`info-banner-${id}`}
    >
      <Info className={cn("h-5 w-5 flex-shrink-0 mt-0.5", iconStyles[variant])} />
      <div className="flex-1 text-lg leading-relaxed pr-8">
        {children}
      </div>
      <button
        onClick={handleDismiss}
        className={cn(
          "absolute top-3 right-3 p-1 rounded-full hover:bg-black/10 transition-colors",
          iconStyles[variant]
        )}
        title="Kapat"
        data-testid={`info-banner-close-${id}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default InfoBanner;
